import React, { useEffect, useState } from "react";
import {
	LineChart,
	Line,
	Area,
	XAxis,
	YAxis,
	CartesianGrid,
	ReferenceLine,
	Tooltip as RechartsTooltip,
	ResponsiveContainer,
	Legend,
	Cell,
	Label,
} from "recharts";
import { supabase } from "@/lib/supabase";

type CheckInTrend = {
	date: string;
	mood_average: number;
	mood_high: number;
	mood_low: number;
	mood_spread: number;
	stress_average: number;
	participation_rate: number;
	individual_moods: number[];
};

export function TeamMoodOverview({ userIds }: { userIds: string[] }) {
	const [dailyTrends, setDailyTrends] = useState<CheckInTrend[]>([]);
	const [loading, setLoading] = useState(false);

	// stabilize userIds input to prevent re-fetch when parent recreates arrays
	const userIdsKey = React.useMemo(
		() => (userIds || []).slice().join(","),
		[userIds],
	);

	useEffect(() => {
		let mounted = true;
		const fetchLast28Days = async () => {
			const ids = userIdsKey ? userIdsKey.split(",").filter(Boolean) : [];
			if (!ids || ids.length === 0) {
				if (mounted) setDailyTrends([]);
				return;
			}

			try {
				if (mounted) setLoading(true);
				const now = new Date();
				const start = new Date(now.getTime() - 27 * 24 * 60 * 60 * 1000);

				// Use edge function to bypass RLS and fetch check-ins for all group members
				const { data: response, error } = await supabase.functions.invoke(
					"fetch-group-checkins",
					{
						body: {
							userIds: ids,
							startDate: start.toISOString(),
						},
					},
				);

				if (error) {
					console.error(
						"Error fetching 28-day checkins via edge function:",
						error,
					);
					if (mounted) {
						setDailyTrends([]);
						setLoading(false);
					}
					return;
				}

				const checkins = response?.checkins || [];

				const dayMap: Record<string, any[]> = {};
				for (let i = 0; i < 28; i++) {
					const d = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
					const key = d.toISOString().slice(0, 10);
					dayMap[key] = [];
				}

				(checkins || []).forEach((ci: any) => {
					const key = new Date(ci.created_at).toISOString().slice(0, 10);
					if (!dayMap[key]) dayMap[key] = [];
					dayMap[key].push(ci);
				});

				// Add debugging to see what data we're getting
				console.log("📊 TeamMoodOverview Debug (via edge function):");
				console.log("  - Total user IDs requested:", ids.length, ids);
				console.log("  - Total check-ins fetched:", checkins.length);
				console.log(
					"  - Unique users with check-ins:",
					new Set(checkins.map((c: any) => c.user_id)).size,
				);
				console.log(
					"  - User IDs with check-ins:",
					Array.from(new Set(checkins.map((c: any) => c.user_id))),
				);

				// Log sample day data
				const sampleDay = Object.keys(dayMap).find(
					(key) => dayMap[key].length > 0,
				);
				if (sampleDay) {
					const sampleItems = dayMap[sampleDay];
					console.log(`  - Sample day (${sampleDay}):`, {
						checkInCount: sampleItems.length,
						uniqueUsers: new Set(sampleItems.map((it: any) => it.user_id)).size,
						userIds: sampleItems.map((it: any) => it.user_id),
						moods: sampleItems.map((it: any) => it.general_feeling),
						calculatedHigh: Math.max(
							...sampleItems.map((it: any) => Number(it.general_feeling || 0)),
						),
						calculatedLow: Math.min(
							...sampleItems.map((it: any) => Number(it.general_feeling || 0)),
						),
						calculatedAvg:
							sampleItems.reduce(
								(s: number, it: any) => s + Number(it.general_feeling || 0),
								0,
							) / sampleItems.length,
					});
				}

				const trends: CheckInTrend[] = Object.keys(dayMap)
					.sort()
					.map((dateKey) => {
						const items = dayMap[dateKey] || [];
						const individual_moods = items.map((it: any) =>
							Number(it.general_feeling || 0),
						);
						const participation_rate =
							ids.length > 0
								? Math.round(
										(new Set(items.map((it: any) => it.user_id)).size /
											ids.length) *
											100,
									)
								: 0;
						const mood_average = individual_moods.length
							? individual_moods.reduce((s: number, v: number) => s + v, 0) /
								individual_moods.length
							: 0;
						const mood_high = individual_moods.length
							? Math.max(...individual_moods)
							: 0;
						const mood_low = individual_moods.length
							? Math.min(...individual_moods)
							: 0;
						const mood_spread = mood_high - mood_low;

						return {
							date: dateKey,
							mood_average: Number(mood_average.toFixed(2)),
							mood_high: Number(mood_high.toFixed(2)),
							mood_low: Number(mood_low.toFixed(2)),
							mood_spread: Number(mood_spread.toFixed(2)),
							stress_average: 0,
							participation_rate,
							individual_moods,
						} as CheckInTrend;
					});

				if (mounted) setDailyTrends(trends);
			} catch (err) {
				console.error("Error computing daily trends:", err);
				if (mounted) setDailyTrends([]);
			} finally {
				if (mounted) setLoading(false);
			}
		};

		fetchLast28Days();
		return () => {
			mounted = false;
		};
	}, [userIdsKey]);

	const chartData = React.useMemo(
		() =>
			dailyTrends.map((t) => ({
				date: t.date,
				labelDate: new Date(t.date).toLocaleDateString("en-US", {
					month: "short",
					day: "numeric",
				}),
				weekdayLabel: new Date(t.date).toLocaleDateString("en-US", {
					weekday: "short",
				}),
				mood_average: t.mood_average,
				// Only include high/low if there's actual data (individual_moods.length > 0)
				mood_high: (t.individual_moods || []).length > 0 ? t.mood_high : null,
				mood_low: (t.individual_moods || []).length > 0 ? t.mood_low : null,
				mood_low_base: t.mood_low,
				mood_range: Math.max(0, t.mood_high - t.mood_low),
				participation: t.participation_rate,
				outliers: t.individual_moods.filter(
					(m) => Math.abs(m - t.mood_average) > 1.5,
				).length,
			})),
		[dailyTrends],
	);

	const trendsAnalysis = React.useMemo(() => {
		const nonEmpty = dailyTrends.filter(
			(t) => (t.individual_moods || []).length > 0,
		);
		let mostPositiveDay = null as any;
		let mostNegativeDay = null as any;
		let highestSpreadDay = null as any;

		if (nonEmpty.length > 0) {
			mostPositiveDay = nonEmpty.reduce(
				(best, t) => (!best || t.mood_average > best.mood_average ? t : best),
				nonEmpty[0],
			);
			mostNegativeDay = nonEmpty.reduce(
				(best, t) => (!best || t.mood_average < best.mood_average ? t : best),
				nonEmpty[0],
			);
			highestSpreadDay = nonEmpty.reduce(
				(best, t) => (!best || t.mood_spread > best.mood_spread ? t : best),
				nonEmpty[0],
			);
		}

		const weekdaySums: Record<number, { sum: number; count: number }> = {};
		dailyTrends.forEach((t) => {
			const d = new Date(t.date);
			const wd = d.getUTCDay();
			if (!weekdaySums[wd]) weekdaySums[wd] = { sum: 0, count: 0 };
			if (t.individual_moods.length > 0) {
				weekdaySums[wd].sum += t.mood_average;
				weekdaySums[wd].count += 1;
			}
		});

		const names = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
		const weekdayAverages: Record<string, number> = {};
		for (let i = 0; i < 7; i++) {
			const rec = weekdaySums[i];
			weekdayAverages[names[i]] =
				rec && rec.count > 0 ? Number((rec.sum / rec.count).toFixed(2)) : 0;
		}

		// Trend direction
		const first7 = nonEmpty.slice(0, 7);
		const last7 = nonEmpty.slice(-7);
		let trendDirection: "Improving" | "Worsening" | "Stable" = "Stable";
		if (first7.length > 0 && last7.length > 0) {
			const avgFirst =
				first7.reduce((s, t) => s + t.mood_average, 0) / first7.length;
			const avgLast =
				last7.reduce((s, t) => s + t.mood_average, 0) / last7.length;
			if (avgLast > avgFirst + 0.2) trendDirection = "Improving";
			else if (avgLast < avgFirst - 0.2) trendDirection = "Worsening";
		}

		return {
			mostPositiveDay: mostPositiveDay
				? { date: mostPositiveDay.date, avg: mostPositiveDay.mood_average }
				: null,
			mostNegativeDay: mostNegativeDay
				? { date: mostNegativeDay.date, avg: mostNegativeDay.mood_average }
				: null,
			highestSpreadDay: highestSpreadDay
				? { date: highestSpreadDay.date, spread: highestSpreadDay.mood_spread }
				: null,
			weekdayAverages,
			trendDirection,
		};
	}, [dailyTrends]);

	const colors = { high: "#10B981", low: "#EF4444", avg: "#2563eb" };

	const memoizedChart = React.useMemo(() => {
		// Find the data points for key observations
		const mostPositiveDataPoint = trendsAnalysis.mostPositiveDay
			? chartData.find((d) => d.date === trendsAnalysis.mostPositiveDay?.date)
			: null;
		const mostNegativeDataPoint = trendsAnalysis.mostNegativeDay
			? chartData.find((d) => d.date === trendsAnalysis.mostNegativeDay?.date)
			: null;
		const highestSpreadDataPoint = trendsAnalysis.highestSpreadDay
			? chartData.find((d) => d.date === trendsAnalysis.highestSpreadDay?.date)
			: null;

		return (
			<div className="h-64 mb-4">
				<ResponsiveContainer width="100%" height="100%">
					<LineChart
						data={chartData}
						margin={{ left: 0, right: 20, top: 10, bottom: 30 }}
					>
						<CartesianGrid strokeDasharray="3 3" />
						<XAxis
							dataKey="labelDate"
							tick={(tickProps) => {
								const p = tickProps as any;
								const x = p.x ?? 0;
								const y = (p.y ?? 0) + 6;
								const payload = p.payload || p;

								let label = "";
								let weekday = "";

								if (payload) {
									if (typeof payload.value !== "undefined")
										label = String(payload.value);
									else if (payload.payload && payload.payload.labelDate)
										label = String(payload.payload.labelDate);
									else if (payload.payload && payload.payload.date)
										label = new Date(payload.payload.date).toLocaleDateString(
											"en-US",
											{ month: "short", day: "numeric" },
										);

									if (payload.payload && payload.payload.weekdayLabel)
										weekday = String(payload.payload.weekdayLabel);
									else if (payload.payload && payload.payload.date)
										weekday = new Date(payload.payload.date).toLocaleDateString(
											"en-US",
											{ weekday: "short" },
										);
								}

								return (
									<g transform={`translate(${x}, ${y})`}>
										<text
											x={0}
											y={0}
											textAnchor="middle"
											fontSize={11}
											fill="#374151"
										>
											{label}
										</text>
										<text
											x={0}
											y={14}
											textAnchor="middle"
											fontSize={10}
											fill="#9CA3AF"
										>
											{weekday}
										</text>
									</g>
								);
							}}
							interval={Math.max(0, Math.floor(chartData.length / 7))}
						/>
						<YAxis domain={[-4, 4]} tick={false} axisLine={false} />
						<ReferenceLine
							y={0}
							stroke="#374151"
							strokeWidth={1}
							strokeDasharray="4 4"
						/>
						<Legend verticalAlign="bottom" align="center" height={36} />
						<RechartsTooltip
							content={({ active, payload }) => {
								if (!active || !payload || payload.length === 0) return null;
								const item =
									payload.find((p: any) => p.dataKey === "mood_average") ||
									payload[0];
								const value = item?.value;

								const phraseFromValue = (v: number) => {
									if (v === null || v === undefined || isNaN(v))
										return "No data";
									if (v <= -2.5) return "Getting much worse";
									if (v <= -1.1) return "Getting worse";
									if (v <= -0.5) return "Slightly worse";
									if (v < 0.5) return "About the same";
									if (v < 1.1) return "Slightly better";
									if (v < 2.5) return "Getting better";
									return "Getting much better";
								};

								return (
									<div className="rounded-md border bg-popover px-3 py-2 text-xs shadow-md">
										<div className="font-medium">
											{phraseFromValue(Number(value))}
										</div>
									</div>
								);
							}}
						/>

						{/* High and Low lines - thin lines (render first so they're behind the average) */}
						<Line
							type="monotone"
							dataKey="mood_high"
							stroke={colors.high}
							strokeWidth={1}
							strokeOpacity={0.85}
							strokeDasharray="5 5"
							dot={false}
							connectNulls={false}
							name="Daily High"
						/>
						<Line
							type="monotone"
							dataKey="mood_low"
							stroke={colors.low}
							strokeWidth={1}
							strokeOpacity={0.85}
							strokeDasharray="5 5"
							dot={false}
							connectNulls={false}
							name="Daily Low"
						/>

						<Area
							dataKey="mood_low_base"
							stroke="none"
							fillOpacity={0}
							stackId="rangeBand"
						/>
						<Area
							dataKey="mood_range"
							stroke="none"
							fill="#bfdbfe"
							fillOpacity={0.18}
							stackId="rangeBand"
							name="Range (High-Low)"
						/>

						{/* Average line - blue, most prominent */}
						<Area
							type="monotone"
							dataKey="mood_average"
							stroke={colors.avg}
							fill="#bfdbfe"
							fillOpacity={0.08}
							name="Team Mood Average"
						/>
						<Line
							type="monotone"
							dataKey="mood_average"
							stroke={colors.avg}
							dot={(props: any) => {
								const { cx, cy, payload, index } = props;
								const key = `dot-${payload?.date || index || Math.random()}`;
								const isMostPositive =
									mostPositiveDataPoint &&
									payload.date === mostPositiveDataPoint.date;
								const isMostNegative =
									mostNegativeDataPoint &&
									payload.date === mostNegativeDataPoint.date;
								const isHighestSpread =
									highestSpreadDataPoint &&
									payload.date === highestSpreadDataPoint.date;

								if (isMostPositive) {
									return (
										<g key={key}>
											<circle
												key={`${key}-circle`}
												cx={cx}
												cy={cy}
												r={4}
												fill="#10B981"
												stroke="#10B981"
												strokeWidth={2}
											/>
											<text
												key={`${key}-text`}
												x={cx}
												y={cy - 12}
												textAnchor="middle"
												fontSize={9}
												fill="#10B981"
												fontWeight="bold"
											>
												Most Positive
											</text>
										</g>
									);
								}
								if (isMostNegative) {
									return (
										<g key={key}>
											<circle
												key={`${key}-circle`}
												cx={cx}
												cy={cy}
												r={4}
												fill="#EF4444"
												stroke="#EF4444"
												strokeWidth={2}
											/>
											<text
												key={`${key}-text`}
												x={cx}
												y={cy + 20}
												textAnchor="middle"
												fontSize={9}
												fill="#EF4444"
												fontWeight="bold"
											>
												Most Negative
											</text>
										</g>
									);
								}
								if (isHighestSpread) {
									return (
										<g key={key}>
											<circle
												key={`${key}-circle`}
												cx={cx}
												cy={cy}
												r={4}
												fill="#F59E0B"
												stroke="#F59E0B"
												strokeWidth={2}
											/>
											<text
												key={`${key}-text`}
												x={cx}
												y={cy - 12}
												textAnchor="middle"
												fontSize={9}
												fill="#F59E0B"
												fontWeight="bold"
											>
												Highest Variability
											</text>
										</g>
									);
								}
								return (
									<circle key={key} cx={cx} cy={cy} r={2} fill={colors.avg} />
								);
							}}
							strokeWidth={2.5}
							activeDot={{ r: 3 }}
							name="Team Mood Average"
						/>
					</LineChart>
				</ResponsiveContainer>
			</div>
		);
	}, [chartData, trendsAnalysis]);

	if (loading)
		return (
			<div className="text-sm text-muted-foreground">
				Loading mood analysis...
			</div>
		);
	if (!dailyTrends || dailyTrends.length === 0)
		return (
			<div className="text-sm text-muted-foreground">
				No mood data available for the last 28 days.
			</div>
		);

	return (
		<div>
			<div className="mb-3">
				<h4 className="text-base font-medium">
					Team Mood Overview with Individual Spread
				</h4>
				<p className="text-sm text-muted-foreground mt-1">
					Overview of recent team check-ins and spread of individual scores
				</p>
			</div>

			{memoizedChart}
		</div>
	);
}
